import { createFileRoute } from "@tanstack/react-router";
import { ServiceDetail } from "@/components/ServiceDetail";
import { serviceFaqs } from "@/data/serviceFaqs";
import imgAsset from "@/assets/episode-outdoor-speaker.jpg.asset.json";
import { serviceGraph, abs } from "@/lib/serviceSchema";
import outdoorVideoAsset from "@/assets/outdoor-entertainment-hero.mov.asset.json";
const img = imgAsset.url;
const outdoorVideo = outdoorVideoAsset.url;

export const Route = createFileRoute("/services/outdoor")({
  head: () => ({
    meta: [
      { title: "Outdoor TV, Speakers & Patio AV Install | Apex AV Denver" },
      { name: "description", content: "Weather-rated outdoor 4K TVs, landscape speakers, and pergola lighting built for Colorado weather. Full-sun displays and buried landscape audio." },
      { name: "keywords", content: "outdoor TV installation Denver, outdoor speakers Denver, patio AV installation Denver, landscape speaker installation Denver, weatherproof TV Denver, outdoor entertainment system Denver, pergola lighting Denver, backyard audio Denver, outdoor surround sound Denver, Colorado outdoor AV installer" },
      { property: "og:title", content: "Outdoor Entertainment — Apex AV Denver" },
      { property: "og:description", content: "4K outdoor TVs and landscape speakers built for the Front Range." },
      { property: "og:image", content: abs(img) },
      { name: "twitter:image", content: abs(img) },
      { property: "og:url", content: "https://apexav.com/services/outdoor" },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "Outdoor Entertainment — Apex AV Denver" },
      { name: "twitter:description", content: "4K outdoor TVs and landscape speakers built for the Front Range." },
    ],
    links: [{ rel: "canonical", href: "https://apexav.com/services/outdoor" }, { rel: "preload", as: "image", href: img, fetchPriority: "high" } as never],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceGraph({
            slug: "outdoor",
            name: "Outdoor Entertainment",
            description: "Weatherproof TVs, landscape audio, and patio entertainment in Denver.",
            pageTitle: "Outdoor TV, Speakers & Patio AV Install | Apex AV Denver",
            pageDescription: "Weather-rated outdoor 4K TVs, landscape speakers, and pergola lighting built for Colorado weather. Full-sun displays and buried landscape audio.",
            image: img,
            faqs: serviceFaqs["outdoor"],
            serviceType: "Outdoor Entertainment",
            offerCatalog: [
              "Premium full-sun 4K outdoor displays",
              "Buried landscape speakers and rock-style satellite speakers",
              "Buried subwoofers for full-range outdoor sound",
              "Pergola, deck, and pathway lighting integration",
              "Weather-sealed equipment enclosures and mounts",
              "All-season cabling rated for Colorado UV and freeze-thaw",
            ],
            breadcrumbName: "Outdoor Entertainment",
            extraNodes: [
              {
                "@type": "VideoObject",
                name: "Outdoor Entertainment — Apex AV Denver",
                description: "Weatherproof outdoor TVs, landscape audio, and patio entertainment installed across Denver.",
                thumbnailUrl: [img],
                uploadDate: "2026-06-01T00:00:00-06:00",
                contentUrl: abs(outdoorVideo),
                embedUrl: "https://apexav.com/services/outdoor",
              },
            ],
          })
        ),
      },
    ],
  }),
  component: () => (
    <ServiceDetail
      faqs={serviceFaqs["outdoor"]}
      topics={["outdoor", "av-installation", "whole-home-audio", "tv-mounting", "networking"]}
      eyebrow="Outdoor entertainment"
      title="Built for Colorado weather."
      intro={<>Full-sun <strong>outdoor TV installation in Denver</strong>, buried <strong>landscape speakers</strong>, patio audio, and pergola lighting designed to take Denver's UV, hail, and temperature swings — and still look incredible at game time.</>}
      image={img}
      video={outdoorVideo}
      videoType="video/quicktime"
      imageAlt="Outdoor 4K patio TV and landscape speaker installation at a Castle Pines Denver luxury home"
      brands={["sony", "samsung", "klipsch", "episode", "sanus"]}
      bullets={[
        "Premium full-sun 4K outdoor displays",
        "Buried landscape speakers and rock-style satellite speakers",
        "Buried subwoofers for full-range outdoor sound",
        "Pergola, deck, and pathway lighting integration",
        "Weather-sealed equipment enclosures and mounts",
        "All-season cabling rated for Colorado UV and freeze-thaw",
      ]}
      features={[
        { title: "Full-sun ready", desc: "2,000+ nit screens stay readable on the brightest Front Range afternoon." },
        { title: "Hail-rated", desc: "Hardware rated for Denver's summer storms and winter freeze cycles." },
        { title: "Whole-yard sound", desc: "Even coverage across patio, pool, and lawn — no hot spots, no dead zones." },
      ]}
    />
  ),
});
